import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
    FormTitle, FormSubtitle, FormGroup, FormRow, Label,
    StyledInput, StyledSelect, ErrorText, CheckboxLabel,
    INDUSTRIES, VOLUMES, SOURCE_OF_FUNDS, INTENDED_USE,
    TRANSACTION_TYPES, TRANSACTION_FREQUENCIES, AVERAGE_BALANCES
} from '../styles';

const Step2Operations = () => {
    const { register, watch, formState: { errors } } = useFormContext();
    const isFinancialInstitution = watch('isFinancialInstitution');

    return (
        <div>
            <FormTitle>Operations & Account Purpose</FormTitle>
            <FormSubtitle>Business operations and intended account usage (required for BSA/AML compliance).</FormSubtitle>

            <FormRow>
                <FormGroup>
                    <Label htmlFor="industry">Industry</Label>
                    <StyledSelect {...register('industry')} $error={!!errors.industry}>
                        <option value="">Select an industry</option>
                        {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                    </StyledSelect>
                    {errors.industry && <ErrorText>{errors.industry.message}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="monthlyVolume">Expected Monthly Volume</Label>
                    <StyledSelect {...register('monthlyVolume')} $error={!!errors.monthlyVolume}>
                        <option value="">Select volume</option>
                        {VOLUMES.map(vol => <option key={vol} value={vol}>{vol}</option>)}
                    </StyledSelect>
                    {errors.monthlyVolume && <ErrorText>{errors.monthlyVolume.message}</ErrorText>}
                </FormGroup>
            </FormRow>

            <FormGroup>
                <Label htmlFor="businessDescription">Brief Business Description</Label>
                <StyledInput
                    as="textarea"
                    rows="4"
                    {...register('businessDescription')}
                    placeholder="Describe your main products or services..."
                    style={{ resize: 'vertical', minHeight: '100px' }}
                    $error={!!errors.businessDescription}
                />
                {errors.businessDescription && <ErrorText>{errors.businessDescription.message}</ErrorText>}
            </FormGroup>

            <FormRow>
                <FormGroup>
                    <Label htmlFor="sourceOfFunds">Source of Funds</Label>
                    <StyledSelect {...register('sourceOfFunds')} $error={!!errors.sourceOfFunds}>
                        <option value="">Select source</option>
                        {SOURCE_OF_FUNDS.map(s => <option key={s} value={s}>{s}</option>)}
                    </StyledSelect>
                    {errors.sourceOfFunds && <ErrorText>{errors.sourceOfFunds.message}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="intendedUse">Intended Use of Account</Label>
                    <StyledSelect {...register('intendedUse')} $error={!!errors.intendedUse}>
                        <option value="">Select intended use</option>
                        {INTENDED_USE.map(u => <option key={u} value={u}>{u}</option>)}
                    </StyledSelect>
                    {errors.intendedUse && <ErrorText>{errors.intendedUse.message}</ErrorText>}
                </FormGroup>
            </FormRow>

            <FormRow>
                <FormGroup>
                    <Label htmlFor="transactionTypes">Primary Transaction Types</Label>
                    <StyledSelect {...register('transactionTypes')} $error={!!errors.transactionTypes}>
                        <option value="">Select type</option>
                        {TRANSACTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </StyledSelect>
                    {errors.transactionTypes && <ErrorText>{errors.transactionTypes.message}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="transactionFrequency">Expected Transaction Frequency</Label>
                    <StyledSelect {...register('transactionFrequency')} $error={!!errors.transactionFrequency}>
                        <option value="">Select frequency</option>
                        {TRANSACTION_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </StyledSelect>
                    {errors.transactionFrequency && <ErrorText>{errors.transactionFrequency.message}</ErrorText>}
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="averageBalance">Expected Average Balance</Label>
                    <StyledSelect {...register('averageBalance')} $error={!!errors.averageBalance}>
                        <option value="">Select range</option>
                        {AVERAGE_BALANCES.map(b => <option key={b} value={b}>{b}</option>)}
                    </StyledSelect>
                    {errors.averageBalance && <ErrorText>{errors.averageBalance.message}</ErrorText>}
                </FormGroup>
            </FormRow>

            <FormGroup>
                <Label htmlFor="countriesOfOperation">Countries of Operation <span style={{ opacity: 0.6, fontWeight: 'normal' }}>— Optional</span></Label>
                <StyledInput type="text" {...register('countriesOfOperation')} placeholder="e.g. USA, UK, Canada" />
            </FormGroup>

            <FormGroup>
                <Label htmlFor="website">Company Website <span style={{ opacity: 0.6, fontWeight: 'normal' }}>— Optional</span></Label>
                <StyledInput type="url" {...register('website')} placeholder="https://www.example.com" $error={!!errors.website} />
                {errors.website && <ErrorText>{errors.website.message}</ErrorText>}
            </FormGroup>

            <div style={{ marginTop: '32px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Regulatory Status</h3>
                
                <CheckboxLabel style={{ marginBottom: '16px' }}>
                    <input type="checkbox" {...register('isFinancialInstitution')} />
                    <span>This entity is a registered Financial Institution (Bank, MSB, Crypto Exchange, etc.)</span>
                </CheckboxLabel>

                {isFinancialInstitution && (
                    <div style={{ padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <FormGroup style={{ margin: 0 }}>
                            <Label htmlFor="fincenRegistration">FinCEN / Regulator Registration Details</Label>
                            <StyledInput 
                                type="text" 
                                {...register('fincenRegistration')} 
                                placeholder="Registration number and regulatory body" 
                                $error={!!errors.fincenRegistration} 
                            />
                            {errors.fincenRegistration && <ErrorText>{errors.fincenRegistration.message}</ErrorText>}
                        </FormGroup>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Step2Operations;
